import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, KeyRound, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Link non valido"); return; }
    if (password.length < 8) { toast.error("Password minimo 8 caratteri"); return; }
    if (password !== confirm) { toast.error("Le password non coincidono"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/", { replace: true }), 2400);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col justify-center px-6 py-10">
      <div className="max-w-md w-full mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="reset-back">
          <ArrowLeft className="w-4 h-4" /> Torna al login
        </Link>
        <div className="mt-8 ios-card p-8">
          {done ? (
            <div className="text-center py-4">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="font-display font-black text-2xl mt-4">Password aggiornata!</h1>
              <p className="mt-2 text-sm text-slate-600">Ti reindirizzo al login…</p>
            </div>
          ) : (
            <>
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#12466F] to-[#0A84FF] flex items-center justify-center shadow-lg">
                <KeyRound className="w-6 h-6 text-white" />
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Nuova password</div>
              <h1 className="font-display font-black text-2xl mt-1">Reimposta la password</h1>
              <p className="mt-2 text-sm text-slate-600">Scegli una password sicura (minimo 8 caratteri).</p>
              {!token && (
                <div className="mt-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2">
                  Link non valido — richiedi un nuovo email di reset.
                </div>
              )}
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-slate-500">Nuova password</Label>
                  <Input
                    data-testid="reset-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    required
                    className="rounded-xl mt-1 bg-slate-50 border-slate-200 h-11"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-slate-500">Conferma password</Label>
                  <Input
                    data-testid="reset-confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    minLength={8}
                    required
                    className="rounded-xl mt-1 bg-slate-50 border-slate-200 h-11"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="reset-submit"
                  disabled={busy || !token}
                  className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                >
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aggiorno…</> : "Reimposta password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
